import { getDistance as haversineDist } from 'ol/sphere';
import { VoyageRoutePointFeature, VoyageRouteLineFeature, VoyageRoute } from '../models/voyage-routing-model';
import { VoyagePlan } from '../models/vessel-planning-model';
import { ImportResult } from './types/converters.types';
import { XMLParser } from 'fast-xml-parser';

const NMToMeters = 1852; // Nautical miles -> meters
const KtsToMps = 1852/(60*60);

export class RTEConverter {
    static check(str: string) {
        try {
            const parser = new XMLParser({
                ignoreAttributes: false,
                attributeNamePrefix: '',
                textNodeName: '#text',
                ignoreDeclaration: true,
                parseTagValue: false,
                trimValues: false
            });
            const parsed = parser.parse(str);

            // Check if root is 'route' and has 'passagePlan'
            if (parsed && parsed.route && parsed.route.passagePlan) {
                return parsed;
            }
        } catch (e) {
            // don't care
        }
    }

    static import(str: string, options = {}): ImportResult|undefined {
        const opt = {
            assumeGCAbove: 300,
            ...options
        }

        const toCoord = (str: string) => {
            // {sign}HHHMM.SSS (no padding zeros it seems, e.g. can be "-12345.678" or "0")
            const tmpf = Number.parseFloat(str) / 100;
            return Math.trunc(tmpf) + ((tmpf % 1) * 100 / 60);
        }

        try {
            const route_geometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature> = [];

            const parsed = RTEConverter.check(str) as any;
            if (!parsed || !parsed.route) return;

            let waypoints = parsed.route.waypoints?.waypoint;
            if (!waypoints) return;
            
            // Normalize to array if single waypoint
            if (!Array.isArray(waypoints)) {
                waypoints = [waypoints];
            }

            let prevCoords: [number, number]|null = null;
            const pilotPoints: VoyageRoutePointFeature[] = [];
            let lineType: string | null = null;

            waypoints.forEach((waypoint: any) => {
                const lonText = waypoint.longitude;
                const latText = waypoint.latitude;

                if (lonText === undefined || latText === undefined) return;

                const coords: [number, number] = [
                    toCoord(String(lonText)),
                    toCoord(String(latText))
                ];

                const name = waypoint.details ? String(waypoint.details) : '';
                const minSpeed = waypoint.MinSpeed ? Number.parseFloat(String(waypoint.MinSpeed)) * KtsToMps : undefined;
                const maxSpeed = waypoint.MaxSpeed ? Number.parseFloat(String(waypoint.MaxSpeed)) * KtsToMps : undefined;
                const isPilot = waypoint.isPilot === 'true' || waypoint.isPilot === true;

                const wPoint: VoyageRoutePointFeature = {
                    type: 'Feature',
                    geometry: {
                        coordinates: coords,
                        type: 'Point'
                    },
                    properties: {
                        type: 'Hard',
                        name: name,
                        speed_minimum: minSpeed,
                        speed_maximum: maxSpeed,
                        pilotPoint: isPilot ? true : undefined
                    }
                };

                if (wPoint.properties.pilotPoint) {
                    pilotPoints.push(wPoint);
                }

                if (prevCoords) {
                    const dist = haversineDist(prevCoords, coords) / NMToMeters;

                    let isGC = true;
                    if (lineType) {
                        isGC = lineType === 'GC';
                    } else if (dist < opt.assumeGCAbove) {
                        isGC = false;
                    }

                    route_geometry.push({
                        type: 'Feature',
                        geometry: {
                            coordinates: [prevCoords, coords],
                            type: 'LineString'
                        },
                        properties: {
                            leg_type: isGC ? 'GreatCircle' : 'Rhumbline'
                        }
                    });
                }

                route_geometry.push(wPoint);

                // The RTE route has GC/RL marked on the source wp
                lineType = waypoint.type ? String(waypoint.type) : null;

                prevCoords = coords;
            });

            const extras = RTEConverter._extractExtras(parsed);

            // Add pilot point info
            if (pilotPoints.length === 1) {
                // Assume arrival pilot point
                extras.arrivalPilotPosition = pilotPoints[0].geometry.coordinates;
            } else if (pilotPoints.length >= 2) {
                extras.departurePilotPosition = pilotPoints[0].geometry.coordinates;
                extras.arrivalPilotPosition = pilotPoints[pilotPoints.length-1].geometry.coordinates;
            }

            return {
                route: {
                    routeId: '', // Not available in RTE
                    route_geometry,
                    passagePlan: {
                        description: "",
                        reminders: extras.passagePlan.reminders,
                        notEcdisCompliant: false,
                        encCellsUsed: extras.passagePlan.encCellsUsed,
                        booksAndTables: extras.passagePlan.booksAndTables,
                        chartsAndLightsNotes: extras.passagePlan.chartsAndLightsNotes,
                        sailingDirections: "",
                        listOfRadioSignals: "",
                        listOfLights: "",
                        gpsEcdisRadarMap: "",
                        paperCharts: "",
                        pubCorrectedToWeek: "", 
                        tideTables: "",
                        publicationsNotes: "", 
                        mastersOrders: "",
                        captain: "",
                        navigationalOfficer: "",
                        planDate: null,
                        safetyMessages: {
                            navareaRegions: "",
                            navtexStations: ""
                        },
                        weatherInfo: {
                            navareaRegions: "",
                            navtexStations: "",
                            info: ""
                        },
                        equipmentStatus: {
                            operationalCondition: "",
                            failures: ""
                        },
                        weatherRouting: {
                            required: false,            
                            acknowledged: false,     
                            pilotPointAcknowledged: false 
                        }
                    }
                },
                extras
            };
        } catch (error) {
            console.warn(error);
        }
    }

    static _extractExtras(parsed: any): { [index:string]: any } {
        // reminders
        let reminders, booksAndTables, chartsAndLightsNotes, encCellsUsed;
        
        const route = parsed.route;

        // Handle reminders - each reminder has a Description element
        if (route.reminders?.reminder) {
            let reminderArray = route.reminders.reminder;
            if (!Array.isArray(reminderArray)) {
                reminderArray = [reminderArray];
            }
            reminders = reminderArray.map((r: any) => String(r.Description || '')).join('\n');
        }

        if (route.AdmiraltyListOfLights) {
            chartsAndLightsNotes = String(route.AdmiraltyListOfLights);
        }

        if (route.PilotBooks) {
            booksAndTables = String(route.PilotBooks);
        }

        if (route.ChartsUsed) {
            encCellsUsed = String(route.ChartsUsed);
        }

        if (route.OverviewCharts) {
            const overview = String(route.OverviewCharts);
            encCellsUsed = encCellsUsed ? `${encCellsUsed}, ${overview}` : overview;
        }

        return {
            passagePlan: {
                reminders,
                chartsAndLightsNotes,
                booksAndTables,
                encCellsUsed
            }
        }
    }

    static export(plan: VoyagePlan) {
        console.warn('RTEConverter.export missing!');
    }
}